import { Controller } from '@hotwired/stimulus';
import { getCsrfToken } from '../utils/case_experience_in_rails';
import { browserSupportsDictation, extensionForBlob, insertAtCaret, pickRecorderMimeType } from '../utils/speech_to_text';

export default class extends Controller {
  static targets = ['field', 'toggle', 'meter', 'status'];

  static values = {
    transcribeUrl: String,
  };

  connect() {
    this._disconnected = false;
    if (!browserSupportsDictation()) {
      this.hideDictationUi();
      return;
    }
    this.showDictationUi();
    this._state = 'idle';
    this._chunks = [];
    this._recorder = null;
    this._stream = null;
    this._audioContext = null;
    this._analyser = null;
    this._meterRaf = null;
    this._permissionStatus = null;
    this._onPermissionChange = null;
    this._bindTrackEnded = null;
    if (this.hasToggleTarget) {
      this._toggleDefaultAriaLabel = this.toggleTarget.getAttribute('aria-label') || '';
    }
    this._onOnline = () => this.applyOnlineState();
    this._onOffline = () => this.applyOfflineState();
    window.addEventListener('online', this._onOnline);
    window.addEventListener('offline', this._onOffline);
    this.applyUiState();
    if (!navigator.onLine) {
      this.applyOfflineState();
    }
    void this.watchMicrophonePermission();
    this.observeFieldDisabled();
  }

  fieldTargetConnected() {
    this.observeFieldDisabled();
    this.applyUiState();
  }

  hideDictationUi() {
    this.element.classList.remove('speech-to-text--supported');
    if (this.hasToggleTarget && this.toggleTarget.isConnected) {
      this.toggleTarget.hidden = true;
      this.toggleTarget.setAttribute('hidden', '');
    }
    const toolbar = this.hasToggleTarget ? this.toggleTarget.closest('.speech-to-text__toolbar') : null;
    if (toolbar?.isConnected) toolbar.setAttribute('hidden', '');
    this.element.querySelectorAll('.aqui-chat__input-actions--dictating').forEach((el) => {
      el.hidden = true;
      el.setAttribute('hidden', '');
    });
    if (this.hasMeterTarget && this.meterTarget.isConnected) {
      this.meterTarget.setAttribute('hidden', '');
    }
    if (this.hasStatusTarget && this.statusTarget.isConnected) {
      this.statusTarget.setAttribute('hidden', '');
    }
  }

  showDictationUi() {
    this.element.classList.add('speech-to-text--supported');
    if (this.hasToggleTarget && this.toggleTarget.isConnected) {
      this.toggleTarget.hidden = false;
      this.toggleTarget.removeAttribute('hidden');
    }
    const toolbar = this.hasToggleTarget ? this.toggleTarget.closest('.speech-to-text__toolbar') : null;
    if (toolbar?.isConnected) toolbar.removeAttribute('hidden');
    if (this.hasMeterTarget && this.meterTarget.isConnected) {
      this.meterTarget.removeAttribute('hidden');
    }
    if (this.hasStatusTarget && this.statusTarget.isConnected) {
      this.statusTarget.removeAttribute('hidden');
    }
  }

  disconnect() {
    this._disconnected = true;
    this.teardownRecording({ silent: true });
    this.unobserveFieldDisabled();
    this.unwatchMicrophonePermission();
    if (this._onOnline) window.removeEventListener('online', this._onOnline);
    if (this._onOffline) window.removeEventListener('offline', this._onOffline);
    this._onOnline = null;
    this._onOffline = null;
  }

  applyOfflineState() {
    if (!this.hasToggleTarget || !this.toggleTarget.isConnected) return;
    if (this._state === 'recording' || this._state === 'processing') {
      this.teardownRecording({ silent: true });
    }
    this.toggleTarget.disabled = true;
    this.toggleTarget.setAttribute('aria-label', 'Dictation unavailable offline');
    this.announce('Dictation unavailable offline.');
  }

  applyOnlineState() {
    if (!this.hasToggleTarget || !this.toggleTarget.isConnected) return;
    this.toggleTarget.disabled = this.isToggleDisabled();
    this.toggleTarget.setAttribute('aria-label', this._toggleDefaultAriaLabel || 'Dictate text');
    this.announce('');
  }

  isFormInputDisabled() {
    return this.hasFieldTarget && this.fieldTarget.disabled;
  }

  isToggleDisabled() {
    if (!navigator.onLine) return true;
    if (this._state === 'processing') return true;
    if (this.isFormInputDisabled()) return true;
    return false;
  }

  observeFieldDisabled() {
    if (!this.hasFieldTarget) return;
    this.unobserveFieldDisabled();
    this._fieldDisabledObserver = new MutationObserver(() => {
      this.applyUiState();
    });
    this._fieldDisabledObserver.observe(this.fieldTarget, {
      attributes: true,
      attributeFilter: ['disabled'],
    });
  }

  unobserveFieldDisabled() {
    this._fieldDisabledObserver?.disconnect();
    this._fieldDisabledObserver = null;
  }

  async watchMicrophonePermission() {
    if (!navigator.permissions?.query) return;
    try {
      this._permissionStatus = await navigator.permissions.query({ name: 'microphone' });
      this._onPermissionChange = () => {
        if (this._permissionStatus.state !== 'granted' && this._state === 'recording') {
          this.teardownRecording({ permissionRevoked: true });
        }
      };
      this._permissionStatus.addEventListener('change', this._onPermissionChange);
    } catch {
      // Some browsers reject microphone PermissionsPolicy lookup
    }
  }

  unwatchMicrophonePermission() {
    if (this._permissionStatus && this._onPermissionChange) {
      this._permissionStatus.removeEventListener('change', this._onPermissionChange);
    }
    this._permissionStatus = null;
    this._onPermissionChange = null;
  }

  toggle(event) {
    event?.preventDefault();
    if (!browserSupportsDictation()) return;
    if (!this.hasToggleTarget || !this.toggleTarget.isConnected) return;
    if (!navigator.onLine) {
      this.applyOfflineState();
      return;
    }
    if (this.isToggleDisabled() && this._state !== 'recording') return;
    if (this._state === 'recording') {
      this.stopRecorderAndTranscribe();
      return;
    }
    void this.startRecording();
  }

  cancelRecording(event) {
    event?.preventDefault();
    if (this._state !== 'recording') return;
    this.teardownRecording();
  }

  confirmRecording(event) {
    event?.preventDefault();
    if (this._state !== 'recording') return;
    this.stopRecorderAndTranscribe();
  }

  applyUiState() {
    if (!this.element.isConnected) return;
    this.element.classList.remove(
      'speech-to-text--idle',
      'speech-to-text--recording',
      'speech-to-text--processing',
    );
    this.element.classList.add(`speech-to-text--${this._state}`);
    const toggle = this.hasToggleTarget ? this.toggleTarget : null;
    if (toggle?.isConnected) {
      toggle.disabled = this.isToggleDisabled();
      toggle.setAttribute('aria-pressed', this._state === 'recording' ? 'true' : 'false');
      const busy = this._state === 'processing' ? 'true' : 'false';
      toggle.setAttribute('aria-busy', busy);
    }
    const meter = this.hasMeterTarget ? this.meterTarget : null;
    if (meter?.isConnected) {
      meter.classList.toggle('speech-to-text__meter--active', this._state === 'recording');
    }
  }

  setState(next) {
    this._state = next;
    this.applyUiState();
  }

  announce(message) {
    if (!this.hasStatusTarget || !this.statusTarget.isConnected) return;
    this.statusTarget.textContent = message;
  }

  async startRecording() {
    this._chunks = [];
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      this.setState('idle');
      this.announce('Microphone permission is required to dictate.');
      return;
    }

    this._stream = stream;
    const mimeType = pickRecorderMimeType();
    try {
      this._recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
    } catch {
      stream.getTracks().forEach((t) => t.stop());
      this._stream = null;
      this.setState('idle');
      this.announce('Recording is not supported in this browser.');
      return;
    }

    this._recorder.addEventListener('dataavailable', (ev) => {
      if (ev.data && ev.data.size > 0) {
        this._chunks.push(ev.data);
      }
    });

    this._recorder.addEventListener('error', () => {
      this.teardownRecording({ silent: true });
    });

    this._bindTrackEnded = () => {
      if (this._state === 'recording') {
        this.teardownRecording({ permissionRevoked: true });
      }
    };
    stream.getAudioTracks().forEach((track) => {
      track.addEventListener('ended', this._bindTrackEnded);
    });

    this._recorder.start(250);
    this.setState('recording');
    this.announce('Recording. Press the microphone button again to stop and transcribe.');
    this.startMeter(stream);
  }

  startMeter(stream) {
    if (!this.hasMeterTarget || !this.meterTarget.isConnected) return;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      this._audioContext = new Ctx();
      const source = this._audioContext.createMediaStreamSource(stream);
      this._analyser = this._audioContext.createAnalyser();
      this._analyser.fftSize = 256;
      source.connect(this._analyser);
      const bars = this.meterTarget.querySelectorAll('.speech-to-text__bar');
      const data = new Uint8Array(this._analyser.frequencyBinCount);
      const tick = () => {
        if (!this._analyser || this._state !== 'recording') return;
        if (!this.hasMeterTarget || !this.meterTarget.isConnected) {
          this.stopMeter();
          return;
        }
        this._analyser.getByteFrequencyData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i += 1) sum += data[i];
        const avg = sum / data.length / 255;
        bars.forEach((bar, i) => {
          const jitter = 0.35 + 0.65 * Math.sin((performance.now() + i * 120) / 180);
          const h = Math.min(1, avg * 2.2 * jitter + i * 0.04);
          bar.style.transform = `scaleY(${0.15 + h})`;
        });
        this._meterRaf = requestAnimationFrame(tick);
      };
      this._meterRaf = requestAnimationFrame(tick);
    } catch {
      // Meter is optional
    }
  }

  stopMeter() {
    if (this._meterRaf) {
      cancelAnimationFrame(this._meterRaf);
      this._meterRaf = null;
    }
    if (this._analyser) {
      try {
        this._analyser.disconnect();
      } catch {
        // ignore
      }
      this._analyser = null;
    }
    if (this._audioContext) {
      this._audioContext.close().catch(() => {});
      this._audioContext = null;
    }
    if (this.hasMeterTarget && this.meterTarget.isConnected) {
      this.meterTarget.querySelectorAll('.speech-to-text__bar').forEach((bar) => {
        bar.style.transform = 'scaleY(0.15)';
      });
    }
  }

  stopRecorderAndTranscribe() {
    if (!this._recorder) {
      this.setState('idle');
      return;
    }
    const rec = this._recorder;
    if (rec.state === 'inactive') {
      const blob = new Blob(this._chunks, { type: rec.mimeType || 'audio/webm' });
      this.teardownStreamsOnly();
      void this.postTranscription(blob);
      return;
    }
    rec.addEventListener(
      'stop',
      () => {
        const blobType = rec.mimeType || 'audio/webm';
        const blob = new Blob(this._chunks, { type: blobType });
        this.teardownStreamsOnly();
        void this.postTranscription(blob);
      },
      { once: true },
    );
    try {
      rec.stop();
    } catch {
      this.teardownRecording({ silent: true });
    }
  }

  teardownStreamsOnly() {
    this.stopMeter();
    if (this._stream && this._bindTrackEnded) {
      this._stream.getAudioTracks().forEach((track) => {
        track.removeEventListener('ended', this._bindTrackEnded);
        track.stop();
      });
    }
    this._bindTrackEnded = null;
    this._stream = null;
    this._recorder = null;
    this._chunks = [];
  }

  teardownRecording({ silent = false, permissionRevoked = false } = {}) {
    this.stopMeter();
    if (this._recorder && this._recorder.state !== 'inactive') {
      try {
        this._recorder.stop();
      } catch {
        // ignore
      }
    }
    this._recorder = null;
    if (this._stream && this._bindTrackEnded) {
      this._stream.getAudioTracks().forEach((track) => {
        track.removeEventListener('ended', this._bindTrackEnded);
        track.stop();
      });
    }
    this._bindTrackEnded = null;
    this._stream = null;
    this._chunks = [];
    if (permissionRevoked && !silent) {
      this.announce('Microphone was turned off. Dictation stopped.');
    }
    this.setState('idle');
  }

  async postTranscription(blob) {
    if (!this.hasFieldTarget) {
      this.setState('idle');
      return;
    }
    if (!blob || blob.size === 0) {
      this.setState('idle');
      this.announce('No audio captured. Try again.');
      return;
    }

    this.setState('processing');
    this.announce('Processing, please wait.');

    const filename = `dictation.${extensionForBlob(blob)}`;
    const body = new FormData();
    body.append('audio', blob, filename);

    try {
      const res = await fetch(this.transcribeUrlValue, {
        method: 'POST',
        body,
        headers: {
          Accept: 'application/json',
          'X-CSRF-Token': getCsrfToken(),
        },
        credentials: 'same-origin',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Transcription failed');
      }
      const text = (data.text || '').trim();
      if (this._disconnected) {
        return;
      }
      if (text) {
        if (this.hasFieldTarget && this.fieldTarget.isConnected) {
          try {
            this.fieldTarget.focus();
            insertAtCaret(this.fieldTarget, text);
            this.announce('Transcription inserted. Edit your text as needed.');
          } catch {
            this.announce('Transcription received but could not insert.');
          }
        } else {
          this.announce('Transcription received but text field is unavailable.');
        }
      } else {
        this.announce('No speech detected. Try again.');
      }
    } catch (e) {
      if (!this._disconnected) {
        this.announce(e?.message || 'Transcription failed. Try again.');
      }
    } finally {
      if (!this._disconnected && this.element.isConnected) {
        this.setState('idle');
      }
    }
  }
}
